/** Token-styled modal dialog: backdrop, Escape / outside click to close, labelled title, focus kept inside, focus in on open and back on close */

import { useEffect, useId, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { useT } from '../../i18n'
import { iconButtonClass } from './styles'

interface ModalProps {
  title: string
  onClose: () => void
  children: ReactNode
  /** Width class, e.g. max-w-md (default) or max-w-lg */
  widthClass?: string
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'

/** Keep Tab and Shift+Tab inside the dialog (aria-modal promises the rest of the page is inert) */
function trapTab(e: KeyboardEvent, panel: HTMLElement) {
  const all = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)]
  // Skip hidden controls (e.g. a display:none file input); layout-less environments report none as visible
  const visible = all.filter((el) => el.offsetParent !== null)
  const focusable = visible.length > 0 ? visible : all
  if (focusable.length === 0) {
    e.preventDefault()
    panel.focus()
    return
  }
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement
  if (e.shiftKey && (active === first || active === panel || !panel.contains(active))) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
    e.preventDefault()
    first.focus()
  }
}

export function Modal({ title, onClose, children, widthClass = 'max-w-md' }: ModalProps) {
  const t = useT()
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  // Callers pass inline handlers; keep the latest one without re-running the mount effect (which would steal focus)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  // Close only when the press started on the backdrop, so a text selection dragged out of a field keeps the form
  const pressedBackdrop = useRef(false)

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    // Focus the first field, else the panel, so keyboard users land inside the dialog
    const panel = panelRef.current
    const first = panel?.querySelector<HTMLElement>('input, textarea, select, button[data-autofocus]')
    ;(first ?? panel)?.focus()
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
      if (e.key === 'Tab' && panel) trapTab(e, panel)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      previous?.focus()
    }
  }, [])

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto px-4 py-[10vh]"
      onMouseDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        if (pressedBackdrop.current && e.target === e.currentTarget) onClose()
      }}
    >
      <div className="pointer-events-none fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative w-full ${widthClass} rounded-xl border border-border bg-surface-raised p-5 text-fg shadow-2xl focus:outline-none`}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 id={titleId} className="font-display text-lg font-semibold">
            {title}
          </h2>
          <button type="button" onClick={onClose} aria-label={t('common.close')} className={iconButtonClass}>
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}
